/**
 * PrintableTrackingSheet - Print-optimized view with tracking number,
 * document metadata, and signature line for physical documents.
 */

import React, { useRef, useCallback } from 'react';
import { Printer, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { HardCopyTrackingRecord } from '@/types/hardCopyTracking';

interface PrintableTrackingSheetProps {
  record: HardCopyTrackingRecord;
  onClose: () => void;
}

export function PrintableTrackingSheet({ record, onClose }: PrintableTrackingSheetProps) {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useCallback(() => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Tracking Sheet - ${record.tracking_number}</title>
          <style>
            @page { margin: 1in; }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
              color: #1a1a1a;
              line-height: 1.5;
            }
            .tracking-header {
              text-align: center;
              border-bottom: 2px solid #333;
              padding-bottom: 16px;
              margin-bottom: 24px;
            }
            .tracking-number {
              font-family: 'Courier New', monospace;
              font-size: 28px;
              font-weight: bold;
              letter-spacing: 2px;
              margin: 8px 0;
            }
            .tracking-barcode {
              font-family: 'Courier New', monospace;
              font-size: 14px;
              letter-spacing: 4px;
              color: #666;
            }
            .org-name {
              font-size: 12px;
              color: #666;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            .details-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 12px;
              margin-bottom: 32px;
            }
            .detail-item {
              padding: 8px 0;
              border-bottom: 1px solid #eee;
            }
            .detail-label {
              font-size: 11px;
              color: #888;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .detail-value {
              font-size: 14px;
              font-weight: 500;
              margin-top: 2px;
            }
            .signature-section {
              margin-top: 48px;
              padding-top: 24px;
              border-top: 1px solid #ddd;
            }
            .signature-line {
              display: flex;
              align-items: flex-end;
              gap: 24px;
              margin-top: 48px;
            }
            .sig-field {
              flex: 1;
            }
            .sig-field .line {
              border-bottom: 1px solid #333;
              height: 40px;
            }
            .sig-field .label {
              font-size: 11px;
              color: #888;
              margin-top: 4px;
            }
            .date-field {
              width: 200px;
            }
            .instructions {
              font-size: 11px;
              color: #888;
              margin-top: 32px;
              padding: 12px;
              border: 1px solid #ddd;
              border-radius: 4px;
            }
            .footer {
              margin-top: 40px;
              text-align: center;
              font-size: 10px;
              color: #aaa;
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  }, [record]);

  const today = new Date().toLocaleDateString();

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
        {/* Toolbar */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50 rounded-t-lg">
          <span className="text-sm font-medium">Print Preview</span>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Printable content */}
        <div ref={printRef} className="p-8">
          {/* Header */}
          <div className="tracking-header">
            <div className="org-name">Document Tracking Sheet</div>
            <div className="tracking-number">{record.tracking_number}</div>
            <div className="tracking-barcode">
              ||| {record.tracking_number.split('').join(' ')} |||
            </div>
          </div>

          {/* Details grid */}
          <div className="details-grid">
            <div className="detail-item">
              <div className="detail-label">Member / Staff</div>
              <div className="detail-value">{record.target_name || `${record.target_type} #${record.target_id}`}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Date Generated</div>
              <div className="detail-value">{record.generated_at ? new Date(record.generated_at).toLocaleDateString() : today}</div>
            </div>
            {record.program_name && (
              <div className="detail-item">
                <div className="detail-label">Program</div>
                <div className="detail-value">{record.program_name}</div>
              </div>
            )}
            <div className="detail-item">
              <div className="detail-label">Generated By</div>
              <div className="detail-value">{record.generated_by_name || '-'}</div>
            </div>
            {record.notes && (
              <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                <div className="detail-label">Notes</div>
                <div className="detail-value">{record.notes}</div>
              </div>
            )}
          </div>

          {/* Signature section */}
          <div className="signature-section">
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
              Signature
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>
              By signing below, I acknowledge that I have reviewed this document.
            </div>

            <div className="signature-line">
              <div className="sig-field">
                <div className="line"></div>
                <div className="label">Signature</div>
              </div>
              <div className="sig-field date-field">
                <div className="line"></div>
                <div className="label">Date</div>
              </div>
            </div>

            <div className="signature-line" style={{ marginTop: '32px' }}>
              <div className="sig-field">
                <div className="line"></div>
                <div className="label">Printed Name</div>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="instructions">
            <strong>Instructions for staff:</strong> After this document is signed, record the
            signature date in the system using tracking number <strong>{record.tracking_number}</strong>,
            then scan and upload the signed copy.
          </div>

          {/* Footer */}
          <div className="footer">
            Tracking # {record.tracking_number} &middot; Generated {today}
          </div>
        </div>
      </div>
    </div>
  );
}
